import React, { useRef } from "react";
import { createStyles, Header, Avatar, Menu, Divider, Text } from "@mantine/core";
import { useState } from "react";
import { useNavigate } from "react-router";
import arc from "../assets/img/arc.png";
import { supportedModules } from "../stores/constants";
import { useQuery, useStore } from "../stores/store";
import { Module, QueryKeys } from "../models/models";
import { observer } from "mobx-react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Icon } from "../custom-components/icons";
import { faDownload, faFileExport, faFileImport, faImage, faShieldHalved, faUpload, faUsers } from "@fortawesome/pro-duotone-svg-icons";
import { useElementSize } from "@mantine/hooks";
import ExportImportModule, { ExportImportHandle } from "./app-container-components/export-import-module";
import theme from "../stores/theme";
import LookupDataUploader, { LookupDataUploaderHandle } from "./app-container-components/lookup-data-upload";
import { useAuthContext } from "../stores/auth-store";
import ImageUpload, { ImageUploadHandle } from "../custom-components/image-upload";
import t from "../stores/language";

const useStyles = createStyles((theme) => ({
  header: {
    display: "grid",
    padding: theme.other.arc.paddings[2],
    alignItems: "center",
    gridTemplateColumns: `${theme.other?.siderBarWidth}px 2fr 200px 80px`,
  },
  arcLogo: {
    gridColumn: "1/2",
  },
  itemContainer: {
    display: "flex",
    margin: `${theme.other.arc.margins[0]} ${theme.other.arc.margins[2]}`,
    gridColumn: "2/3",
  },
  clientLogo: {
    gridColumn: "3/4",
  },
  profile: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gridColumn: "4/5",
  },
  link: {
    display: "block",
    color: theme.other.arc.colors[0],
    marginRight: theme.other.arc.margins[4],
    cursor: "grab",
    fontSize: theme.fontSizes.xl,
    fontWeight: "bold",
  },
  logo: {
    width: "auto",
    height: theme.other.imageHeight.lg,
    cursor: "pointer",
  },
  clientLogoImage: {
    width: "auto",
    height: theme.other.imageHeight.lg,
  },
  menuItem: {
    gap: theme.other.arc.margins[1],
    padding: theme.other.arc.paddings[2],
  },
}));

const getMenuUrl = (modules: Module[], menuItem: string) => {
  let id = 0;
  const firstModule = supportedModules[0];
  const parent = modules.find((d) => d.name === firstModule.title);
  if (parent != null) {
    const module = modules.find((D) => D.parentId === parent.id);
    if (module != null) {
      id = module.id;
    }
  }
  let url = "";
  switch (menuItem) {
    case "Module Builder":
      url = `/main/formulaEditor/${id}`;
      break;
    case "Reports":
      url = `/reports/${id}`;
      break;
    default:
      break;
  }
  return url;
};

export const getUserTitle = (name: string) => {
  const titleLength = name.trim().split(" ").length;
  if (titleLength > 1) {
    const s = name.split(" ").filter((d) => d !== "");
    return s[0][0].toUpperCase() + s[titleLength - 1][0].toUpperCase();
  } else {
    return name.substr(0, 2).toUpperCase();
  }
};

export const imageLoader = (path: string) =>
  new Promise<string | null>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(path);
    image.onerror = reject;
    image.src = path;
  });

export const TopMenu = observer(function TopMenu() {
  const { classes } = useStyles();
  const auth = useAuthContext();
  const { store, authStore } = useStore();
  const { ref } = useElementSize();
  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const exportImportRef = useRef<ExportImportHandle>(null);
  const lookupDataUploaderRef = useRef<LookupDataUploaderHandle>(null);
  const logoUploadRef = useRef<ImageUploadHandle>(null);
  const navigateTo = useNavigate();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: [QueryKeys.User],
    queryFn: () => {
      return authStore.GetUserById(authStore.user?.profile.sub ?? "1");
    },
  });

  useQuery({
    queryKey: [QueryKeys.Logo],
    queryFn: () => {
      return store.GetClientLogoUrl();
    },
    onSuccess: (data) => {
      (async () => {
        try {
          const image = await imageLoader(data);
          setLogoUrl(image);
        } catch {
          setLogoUrl(null);
        }
      })();
    },
  });

  useQuery({
    queryKey: [QueryKeys.ProfileImage],
    queryFn: () => {
      return authStore.GetProfileImageUrl(authStore.user?.profile.sub ?? "1");
    },
    onSuccess: (data) => {
      (async () => {
        try {
          const image = await imageLoader(data);
          setProfileImage(image);
        } catch {
          setProfileImage(null);
        }
      })();
    },
  });
  const getModules = (userType: number, modules: Module[]): MenuGroup[] => {
    if (userType === 1)
      return [
        {
          link: "/main",
          label: "Home",
          items: [],
        },

        {
          link: getMenuUrl(modules, "Module Builder"),
          label: "Module Builder",
          items: [],
        },
        {
          link: getMenuUrl(modules, "Reports"),
          label: "Reports",
          items: [],
        },
        {
          link: "",
          label: "Administration",
          items: [
            {
              title: "Import",
              icon: faFileImport,
              action() {
                exportImportRef.current?.show(true);
              },
            },
            {
              title: "Export",
              icon: faFileExport,
              action() {
                exportImportRef.current?.show(false);
              },
            },
            {
              title: "Master Data Upload",
              icon: faUpload,
              action() {
                lookupDataUploaderRef.current?.show();
              },
            },
            {
              title: "Master Insurance Data",
              icon: faShieldHalved,
              action() {
                navigateTo("/insurance/master");
              },
            },
            {
              title: "Logo Upload",
              icon: faImage,
              action() {
                logoUploadRef.current?.show();
              },
            },
            {
              title: "Users",
              icon: faUsers,
              action() {
                navigateTo("/users");
              },
            },
            {
              title: "Download History",
              icon: faDownload,
              action() {
                navigateTo("/reportArchives");
              },
            },
          ],
        },
        {
          link: "/contact-us",
          label: "Contact Us",
          items: [],
        },
      ];
    return [
      {
        link: "/main",
        label: "Home",
        items: [],
      },

      {
        link: getMenuUrl(modules, "Reports"),
        label: "Reports",
        items: [],
      },
      {
        link: "/contact-us",
        label: "Contact Us",
        items: [],
      },
    ];
  };
  // if user type is 1 then user is admin
  const menuItems = getModules(user?.userType ?? 2, store.modules).filter((item) => item.label != "");

  const uploadLogo = async (file: File) => {
    const result = await store.UploadLogo(file);
    return result;
  };

  return (
    <Header height={80} className={classes.header} fixed={false}>
      <div className={classes.arcLogo}>
        <img onClick={() => navigateTo("/main")} className={classes.logo} src={arc} alt="logo" />
      </div>
      <MenuComponent ref={ref} menuItems={menuItems} />
      <div className={classes.clientLogo}>
        <img className={classes.clientLogoImage} src={logoUrl ?? ""} />
      </div>
      <div className={classes.profile}>
        <Menu position={"bottom-end"} opened={menuOpened} shadow="md" width={200} closeOnClickOutside onClose={() => setMenuOpened(false)}>
          <Menu.Target>
            <Avatar
              onClick={() => {
                setMenuOpened(!menuOpened);
              }}
              color="blue"
              radius="lg"
              src={profileImage}
            >
              {getUserTitle(user?.name ?? "")}
            </Avatar>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() => {
                auth.signoutRedirect();
              }}
            >
              Logout
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                navigateTo(`/user/${user?.id ?? "1"}`);
              }}
            >
              Profile
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
      <ExportImportModule ref={exportImportRef} />
      <LookupDataUploader ref={lookupDataUploaderRef} />
      <ImageUpload ref={logoUploadRef} title={t("Upload Logo")} onUpload={uploadLogo} />
    </Header>
  );
});

interface MenuItem {
  icon: IconProp;
  title: string;
  action: () => void;
}
interface MenuGroup {
  label: string;
  items: MenuItem[];
  link: string;
}
const MenuComponent = observer(
  React.forwardRef<HTMLDivElement, { menuItems: MenuGroup[] }>(function MenuComponent({ menuItems }, ref) {
    const { classes } = useStyles();
    const navigateTo = useNavigate();

    const onMenuClick = (link: string) => {
      navigateTo(link);
    };
    return (
      <div ref={ref} className={classes.itemContainer}>
        {menuItems.map((menuItem, i) => (
          <>
            {menuItem.items.length > 0 ? (
              <Menu width={200} shadow="lg" key={i}>
                <Menu.Target>
                  <a className={classes.link}>{menuItem.label}</a>
                </Menu.Target>
                <Menu.Dropdown>
                  {menuItem.items.map((item, ix) => (
                    <Menu.Item
                      className={classes.menuItem}
                      onClick={() => item.action()}
                      key={ix}
                      icon={<Icon size="xl" color={theme.other?.arc.colors[1]} icon={item.icon} />}
                    >
                      <Text size={theme.fontSizes?.sm}>{item.title}</Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ) : (
              <a
                key={i}
                onClick={() => {
                  onMenuClick(menuItem.link);
                }}
                className={classes.link}
              >
                {menuItem.label}
              </a>
            )}
            {i + 1 === menuItems.length ? null : <Divider size="md" mr={20} orientation="vertical" />}
          </>
        ))}
      </div>
    );
  })
);
